import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import axios from 'axios';

import { clearUserData } from '@/utils/UserDataStore';

// ============================================================================
// CONSTANTS
// ============================================================================

// Environment configuration
const BASE_URL = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER;
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Storage keys - Only non-sensitive data in localStorage
// Both access and refresh tokens are in httpOnly cookies (managed by backend)
const STORAGE_KEYS = {
  USER_ID: 'user_id',
  USER_DATA: 'user_data', // Non-sensitive user info (name, email, permissions)
} as const;

// API endpoints
const API_ENDPOINTS = {
  LOGIN: '/auth/login',
  REFRESH_TOKEN: '/auth/refresh-access',
  LOGOUT: '/auth/logout',
  VERIFY: '/auth/verify', // Verify if user is authenticated
} as const;

// HTTP status codes
const HTTP_STATUS = {
  OK: 200,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  SESSION_EXPIRED: 419,
  SERVER_ERROR: 500,
} as const;

// Error codes
const ERROR_CODES = {
  NETWORK: 'ERR_NETWORK',
  TIMEOUT: 'ECONNABORTED',
} as const;

// ============================================================================
// TYPES
// ============================================================================

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

interface TokenRefreshResponse {
  message: string;
  // Backend responds with success message, tokens are set as httpOnly cookies
}

interface UserData {
  id: string;
  email: string;
  name: string;
  permissions?: string[];
  // Add other non-sensitive fields as needed
  [key: string]: any;
}

interface VerifyAuthResponse {
  authenticated: boolean;
  user: UserData | null;
  message?: string; // For mobile client warning
}

interface ErrorData {
  message?: string;
  error?: string | { message?: string; requiredPermissions?: string[] };
  requiredPermissions?: string[];
  userPermissions?: string[];
  path?: string;
  endpoint?: string;
  statusCode?: number;
}

// ============================================================================
// TOKEN REFRESH STATE MANAGEMENT
// ============================================================================

/**
 * Global state for token refresh management
 * Prevents multiple simultaneous refresh attempts
 */
const tokenRefreshState = {
  isRefreshing: false,
  subscribers: [] as Array<() => void>,
};

/**
 * Add a callback to be executed when token is refreshed
 * Used to retry failed requests after token refresh
 */
const subscribeToTokenRefresh = (callback: () => void): void => {
  tokenRefreshState.subscribers.push(callback);
};

/**
 * Notify all waiting requests that token has been refreshed
 * Clears the subscriber queue after notification
 */
const notifyTokenRefreshSubscribers = (): void => {
  tokenRefreshState.subscribers.forEach((callback) => callback());
  tokenRefreshState.subscribers = [];
};

/**
 * Reset token refresh state on error
 */
const resetTokenRefreshState = (): void => {
  tokenRefreshState.isRefreshing = false;
  tokenRefreshState.subscribers = [];
};

// ============================================================================
// API INSTANCE CONFIGURATION
// ============================================================================

/**
 * Create axios instance with base configuration
 * withCredentials MUST be true to send/receive cookies
 *
 * Security Architecture:
 * - Both access and refresh tokens are httpOnly cookies
 * - Cookies are automatically sent with every request
 * - No tokens are ever exposed to JavaScript (XSS protection)
 * - Backend validates tokens on every request
 */
const api: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/v1/`,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true, // CRITICAL: Required for cookie-based auth
  headers: {
    // NOTE: Don't set Content-Type here as default
    // It will be set per request or by the browser for FormData
    Accept: 'application/json',
  },
});

// ============================================================================
// USER DATA MANAGEMENT (Non-sensitive only)
// ============================================================================

/**
 * Get stored user data (non-sensitive)
 * This is for UI display only, NOT for authentication
 * Authentication is handled by httpOnly cookies
 */
const getUserData = (): UserData | null => {
  const data = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  return data ? JSON.parse(data) : null;
};

/**
 * Save user data (non-sensitive)
 * Only store data needed for UI display
 * NEVER store tokens or sensitive credentials
 */
const saveUserData = (userData: UserData): void => {
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  localStorage.setItem(STORAGE_KEYS.USER_ID, userData.id);
};

/**
 * Check if user appears to be logged in (UI hint only)
 * Real authentication status is verified by backend
 * This is just for routing and UI state
 */
const isUserLoggedIn = (): boolean => {
  return Boolean(localStorage.getItem(STORAGE_KEYS.USER_ID));
};

/**
 * Clear all client-side data
 * Note: This doesn't clear httpOnly cookies - backend must do that
 */
const clearAuthData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  clearUserData();
};

/**
 * Logout user and clear server-side session
 * Backend will clear httpOnly cookies containing tokens
 */
const performLogout = async (): Promise<void> => {
  try {
    // Call logout endpoint to clear server session and cookies
    await axios.post(
      `${BASE_URL}/v1${API_ENDPOINTS.LOGOUT}`,
      {},
      {
        withCredentials: true,
        timeout: 5000, // 5 second timeout for logout
        // Add a custom flag to bypass interceptor for logout
        headers: {
          'X-Skip-Interceptor': 'true',
        },
      },
    );
  } catch (error: any) {
    // 401 is expected for logout when token is expired - that's OK
    if (error?.response?.status === 401) {
    } else {
      console.error('Logout request failed:', error);
    }
  } finally {
    // Clear client-side data regardless of server response
    clearAuthData();
  }
};

// ============================================================================
// ERROR HANDLERS
// ============================================================================

/**
 * Check if error is a network/connection error
 */
export const isConnectionError = (error: AxiosError): boolean => {
  // No response means network error
  if (!error.response) {
    const errorCode = error.code;
    const errorMessage = error.message;

    return (
      errorCode === ERROR_CODES.NETWORK ||
      errorCode === ERROR_CODES.TIMEOUT ||
      errorMessage.includes('Network Error') ||
      errorMessage.includes('timeout')
    );
  }

  // Server errors (5XX) are also connection issues
  const status = error.response.status;
  return status >= HTTP_STATUS.SERVER_ERROR;
};

/**
 * Check if error is a permission error (403)
 */
const isPermissionError = (errorData: ErrorData): boolean => {
  const message = errorData?.message || errorData?.error?.toString() || '';

  return Boolean(
    errorData?.requiredPermissions ||
      message.includes('Access denied') ||
      message.includes('Insufficient permissions') ||
      message.includes('User has no permissions') ||
      message.includes('does not have any of the required permissions'),
  );
};

/**
 * Check if error is a business logic error (should not cause logout)
 */
const isBusinessLogicError = (errorData: ErrorData): boolean => {
  const message = errorData?.message || '';

  return (
    message.includes('locked') ||
    message.includes('Cannot unassign clients') ||
    message.includes('because the assigned telesales employees')
  );
};

// ============================================================================
// NAVIGATION HELPERS
// ============================================================================

/**
 * Redirect to login page with return URL
 */
const redirectToLogin = async (
  skipRedirectParam: boolean = false,
): Promise<void> => {
  // Perform logout to clear server session and cookies
  await performLogout();

  // Don't add redirect parameter if we're already on login page or explicitly skipping
  const currentPath = window.location.pathname;
  const isLoginPage =
    currentPath === '/login' || currentPath.startsWith('/login');

  if (skipRedirectParam || isLoginPage) {
    window.location.href = '/login';
  } else {
    const encodedPath = encodeURIComponent(currentPath);
    window.location.href = `/login?redirect=${encodedPath}`;
  }
};

// ============================================================================
// TOKEN REFRESH
// ============================================================================

/**
 * Refresh authentication tokens
 *
 * Security flow:
 * 1. Both access and refresh tokens are in httpOnly cookies
 * 2. Cookies are automatically sent with withCredentials: true
 * 3. Backend validates refresh token and rotates both tokens
 * 4. New tokens are set as httpOnly cookies by backend
 * 5. Response only confirms success, no tokens in response body
 */
const refreshAuthToken = async (): Promise<boolean> => {

  try {
    // Both tokens are in httpOnly cookies - sent automatically
    const response = await axios.post<TokenRefreshResponse>(
      `${BASE_URL}/v1${API_ENDPOINTS.REFRESH_TOKEN}`,
      {}, // Empty body - all tokens are in cookies
      {
        withCredentials: true, // CRITICAL: Must be true to send cookies
        timeout: 15000, // 15 second timeout for refresh (increased for better reliability)
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    );

    // Validate response status
    if (response.status !== HTTP_STATUS.OK) {
      console.error(
        '🔴 [INTERCEPTOR] Token refresh failed - invalid status:',
        response.status,
      );
      throw new Error(`Token refresh failed with status: ${response.status}`);
    }

    // Validate response data - backend responds with success message
    if (!response.data || !response.data.message) {
      console.error(
        '🔴 [INTERCEPTOR] Token refresh failed - no response data:',
        response.data,
      );
      throw new Error('Token refresh response missing data');
    }

    // Check if the message indicates success
    const message = response.data.message.toLowerCase();
    if (!message.includes('refresh') && !message.includes('success')) {
      console.error(
        '🔴 [INTERCEPTOR] Token refresh failed - unexpected message:',
        response.data.message,
      );
      throw new Error(`Token refresh failed: ${response.data.message}`);
    }

    // Tokens are already set as httpOnly cookies by backend
    // No need to store anything in JavaScript
    return true;
  } catch (error: any) {
    const status = error.response?.status;
    const isNetworkError = !error.response;

    console.error('🔴 [INTERCEPTOR] Token refresh error:', {
      message: error.message,
      status,
      data: error.response?.data,
      isNetworkError,
    });

    // Handle specific error cases
    if (status === HTTP_STATUS.UNAUTHORIZED) {
      // This will be caught by handle401Error and trigger redirect to login
    } else if (isNetworkError) {
      // Network errors might be temporary, let the retry logic handle it
    } else if (status >= HTTP_STATUS.SERVER_ERROR) {
      // Server errors might be temporary
    }

    // Re-throw the error to be handled by the caller
    throw error;
  }
};

/**
 * Retry original request after token refresh
 * Cookies with new tokens are automatically sent
 */
const retryRequestAfterRefresh = (
  originalRequest: ExtendedAxiosRequestConfig,
): Promise<AxiosResponse> => {
  // Ensure cookies are sent with retry
  originalRequest.withCredentials = true;

  // Use the api instance to retry (maintains interceptors)
  return api.request(originalRequest);
};

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

/**
 * Add security headers to all requests
 * Tokens are automatically included as cookies by the browser
 */
api.interceptors.request.use(
  (config) => {
    // Always ensure withCredentials for cookie support
    config.withCredentials = true;

    // Handle Content-Type based on data type
    if (config.data instanceof FormData) {
      // CRITICAL: Don't set Content-Type for FormData
      // The browser needs to set it with the boundary parameter

      // Remove ALL Content-Type headers for FormData requests
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
        // Also remove from common headers if exists
        if (config.headers.common) {
          delete config.headers.common['Content-Type'];
          delete config.headers.common['content-type'];
        }
      }

    } else if (config.data && typeof config.data === 'object') {
      // For JSON data, set Content-Type
      if (config.headers) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    // Add CSRF token if available (backend should set this in meta tag or cookie)
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content');
    if (csrfToken && config.headers) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Note: X-Request-ID removed to avoid CORS issues
    // Backend needs to add 'X-Request-ID' to Access-Control-Allow-Headers if needed

    // Add timestamp for request timing
    (config as any).metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  },
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

/**
 * Handle 401 Unauthorized errors with automatic token refresh
 */
const handle401Error = async (
  error: AxiosError<ErrorData>,
  originalRequest: ExtendedAxiosRequestConfig,
): Promise<AxiosResponse | never> => {
  const requestUrl = originalRequest.url || 'unknown';

  // Skip redirect for login requests - let the login form handle the error
  const isLoginRequest = requestUrl.includes(API_ENDPOINTS.LOGIN);
  if (isLoginRequest) {
    return Promise.reject(error);
  }

  // Skip 401 handling for logout requests - logout should succeed even without auth
  const isLogoutRequest = requestUrl.includes(API_ENDPOINTS.LOGOUT);
  if (isLogoutRequest) {
    // Don't redirect here - let the application handle the logout response
    // The logout API functions already handle 401 as success
    return Promise.reject(error);
  }

  // Skip 401 handling for version check requests - they should fail silently when not authenticated
  const isVersionRequest = requestUrl.includes('/app/version/');
  if (isVersionRequest) {
    // Don't trigger token refresh for version checks when user is not authenticated
    return Promise.reject(error);
  }

  // Token refresh request itself failed: refresh token expired
  const isRefreshRequest = requestUrl.includes(API_ENDPOINTS.REFRESH_TOKEN);
  if (isRefreshRequest) {
    resetTokenRefreshState();
    await redirectToLogin();
    return Promise.reject(error);
  }

  // Already retried with new token: the new token is also invalid
  if (originalRequest._retry) {
    resetTokenRefreshState();
    await redirectToLogin();
    return Promise.reject(error);
  }


  // Mark request as retry attempt to prevent infinite loops
  originalRequest._retry = true;
  originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

  // If not already refreshing, start refresh process
  if (!tokenRefreshState.isRefreshing) {
    tokenRefreshState.isRefreshing = true;

    try {
      // Attempt to refresh the access token using the refresh token in cookies
      await refreshAuthToken();

      tokenRefreshState.isRefreshing = false;

      // Notify all waiting requests
      notifyTokenRefreshSubscribers();

      // Retry the original request with new access token (now in cookies)
      return retryRequestAfterRefresh(originalRequest);
    } catch (refreshError: any) {
      console.error('🔴 [INTERCEPTOR] Token refresh failed:', refreshError);
      resetTokenRefreshState();

      const refreshErrorStatus = refreshError?.response?.status;
      const isNetworkError = !refreshError?.response;

      // Handle different types of refresh failures
      if (refreshErrorStatus === HTTP_STATUS.UNAUTHORIZED) {
        await redirectToLogin();
        return Promise.reject(refreshError);
      } else if (isNetworkError) {
        return Promise.reject(refreshError);
      } else if (refreshErrorStatus >= HTTP_STATUS.SERVER_ERROR) {
        return Promise.reject(refreshError);
      } else {
        await redirectToLogin();
        return Promise.reject(refreshError);
      }
    }
  }

  // Queue request while token is being refreshed by another request
  return new Promise((resolve, reject) => {
    subscribeToTokenRefresh(() => {
      retryRequestAfterRefresh(originalRequest).then(resolve).catch(reject);
    });
  });
};

/**
 * Handle 403 Forbidden errors (permission denied)
 */
const handle403Error = async (error: AxiosError<ErrorData>): Promise<never> => {
  const requestUrl = error.config?.url || 'unknown';
  const errorData = error.response?.data;

  // Skip redirect for login requests - let the login form handle the error
  const isLoginRequest = requestUrl.includes(API_ENDPOINTS.LOGIN);
  if (isLoginRequest) {
    return Promise.reject(error);
  }

  if (!errorData) {
    await redirectToLogin();
    return Promise.reject(error);
  }

  // Permission error: show dialog instead of redirecting
  if (isPermissionError(errorData)) {

    const permissionData = {
      message:
        errorData.message ||
        'ليس لديك الصلاحيات المطلوبة للوصول إلى هذا المورد',
      requiredPermissions: errorData.requiredPermissions || [],
      userPermissions: errorData.userPermissions || [],
      endpoint:
        errorData.path ||
        errorData.endpoint ||
        `${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      statusCode: errorData.statusCode || HTTP_STATUS.FORBIDDEN,
      timestamp: new Date().toISOString(),
    };

    // Import dynamically to avoid circular dependencies
    import('@/components/PermissionErrorDialog').then(
      ({ showPermissionError }) => {
        showPermissionError(permissionData);
      },
    );

    return Promise.reject(error);
  }

  // Business logic error: let component handle it
  if (isBusinessLogicError(errorData)) {
    return Promise.reject(error);
  }

  // Other 403 errors: logout
  await redirectToLogin();
  return Promise.reject(error);
};

/**
 * Main response interceptor
 * Handles authentication, permissions, and error scenarios
 */
api.interceptors.response.use(
  // Success: pass through response and log timing
  (response) => {
    // Log request timing in development
    if (import.meta.env.DEV) {
      const config = response.config as any;
      if (config.metadata) {
        const duration = Date.now() - config.metadata.startTime;
        console.debug(
          `✅ API ${config.method?.toUpperCase()} ${config.url}: ${duration}ms`,
        );
      }
    }
    return response;
  },

  // Error: handle various error scenarios
  async (error: AxiosError<ErrorData>) => {
    // Handle connection errors
    if (isConnectionError(error)) {
      return Promise.reject(error);
    }

    // No response: network error
    if (!error.response) {
      console.error('No response from server:', error);
      return Promise.reject(error);
    }

    const { status } = error.response;
    const data = error.response.data as any;
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // In production, ensure error messages are properly extracted
    // Some servers return HTML error pages instead of JSON in production
    if (!import.meta.env.DEV && status >= 400 && status < 600) {
      // Try to extract meaningful error message
      if (typeof data === 'string' && data.indexOf('<!DOCTYPE') !== -1) {
        // HTML response - likely a generic error page
        error.response.data = {
          message:
            status === 500
              ? 'خطأ في الخادم، يرجى المحاولة لاحقاً'
              : status === 404
                ? 'المورد المطلوب غير موجود'
                : status === 403
                  ? 'ليس لديك الصلاحية للوصول لهذا المورد'
                  : status === 401
                    ? 'يرجى تسجيل الدخول مرة أخرى'
                    : 'حدث خطأ في معالجة الطلب',
          statusCode: status,
        } as ErrorData;
      } else if (!data || typeof data !== 'object') {
        // Empty or non-object response
        error.response.data = {
          message: 'حدث خطأ في معالجة الطلب',
          statusCode: status,
        } as ErrorData;
      }
    }

    // Handle specific status codes
    switch (status) {
      case HTTP_STATUS.UNAUTHORIZED:
        return handle401Error(error, originalRequest);

      case HTTP_STATUS.FORBIDDEN:
        return handle403Error(error);

      case HTTP_STATUS.SESSION_EXPIRED: {
        // Skip redirect for login requests
        const isLoginReq = originalRequest.url?.includes(API_ENDPOINTS.LOGIN);
        if (!isLoginReq && data?.error === 'Session expired') {
          await redirectToLogin();
        }
        return Promise.reject(error);
      }

      default:
        // Log unexpected errors in development
        if (import.meta.env.DEV && status >= HTTP_STATUS.SERVER_ERROR) {
          console.error('Server error:', status, error.message);
        }
        return Promise.reject(error);
    }
  },
);

// ============================================================================
// AUTHENTICATION VERIFICATION
// ============================================================================

/**
 * Verify if user is authenticated via backend
 *
 * This calls the /auth/verify endpoint which:
 * 1. Uses JwtGuard to validate the access token from cookies
 * 2. Returns authenticated status and sanitized user data
 * 3. Should be called on app initialization to check auth status
 *
 * Backend behavior:
 * - Validates access token from httpOnly cookie
 * - Returns { authenticated: true, user: {...} } if valid
 * - Returns { authenticated: false, user: null } if invalid
 * - Web clients only (mobile clients get a warning message)
 */
export const verifyAuthentication = async (): Promise<boolean> => {
  try {
    const response = await api.get<VerifyAuthResponse>(API_ENDPOINTS.VERIFY);

    // Check if authentication is confirmed
    if (response.data?.authenticated && response.data?.user) {
      // Save sanitized user data for UI purposes
      saveUserData(response.data.user);
      return true;
    }

    // Not authenticated or mobile client warning
    if (response.data?.message) {
      console.warn('Auth verification:', response.data.message);
    }

    clearAuthData();
    return false;
  } catch (error) {
    // 401 or any error means not authenticated
    console.error('Authentication verification failed:', error);
    clearAuthData();
    return false;
  }
};

/**
 * Initialize authentication on app startup
 * Checks if user has valid session via cookies
 */
const initializeAuth = async (): Promise<UserData | null> => {
  try {
    const isAuthenticated = await verifyAuthentication();

    if (isAuthenticated) {
      return getUserData();
    }

    return null;
  } catch (error) {
    console.error('Auth initialization failed:', error);
    return null;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default api;

// Export utility functions for use in other modules
export {
  clearAuthData,
  getUserData,
  initializeAuth,
  isUserLoggedIn,
  performLogout,
  saveUserData,
};
